import { useState, useRef, useEffect } from "react";
import { IcChat, IcSend } from "./Icons";

// "" → relative URL: dev'de CRA proxy, prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

/**
 * AI Chat Widget — işletme sahibinin verisiyle doğal dilde konuşması.
 *
 * Sadece admin görür (App.js'te isAdmin ile sarılı). Sağ altta yüzen buton;
 * LiveFeed toggle'ının (bottom: 24px) üstünde durur (bottom: 80px).
 * Geçmiş yalnızca bileşen state'inde — sayfa yenilenince sıfırlanır (bilinçli:
 * sohbet geçmişi hassas ciro verisi içerir, kalıcı saklamıyoruz).
 */
export default function ChatWidget() {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [remaining, setRemaining] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next.map(m => ({ role: m.role, content: m.content })) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || `HTTP ${res.status}`);
      setMessages(m => [...m, { role: "assistant", content: d.reply }]);
      if (typeof d.remaining_today === "number") setRemaining(d.remaining_today);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", content: `⚠ ${err.message}`, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const SUGGESTIONS = [
    "Bugün ciro ne kadar?",
    "Bu hafta en çok ne sattık?",
    "En yoğun saatimiz hangisi?",
    "Şu an açık masa var mı?",
  ];

  return (
    <div className="chatw">
      {open && (
        <div className="chatw-panel">
          <div className="chatw-hdr">
            <span className="chatw-title"><IcChat size={15} /> Asistan</span>
            {remaining != null && <span className="chatw-quota">bugün {remaining} hak</span>}
            <button className="chatw-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="chatw-list" ref={listRef}>
            {messages.length === 0 && (
              <div className="chatw-empty">
                <p>Satış verileriniz hakkında soru sorun:</p>
                {SUGGESTIONS.map(s => (
                  <button key={s} className="chatw-chip" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chatw-msg ${m.role}${m.error ? " err" : ""}`}>
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="chatw-msg assistant chatw-typing">
                <span /><span /><span />
              </div>
            )}
          </div>

          <div className="chatw-inputrow">
            <input
              ref={inputRef}
              className="chatw-input"
              placeholder="Sorunuzu yazın…"
              value={input}
              maxLength={500}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              disabled={busy}
            />
            <button className="chatw-send" onClick={() => send()} disabled={busy || !input.trim()}>
              <IcSend size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        className="chatw-toggle"
        onClick={() => setOpen(o => !o)}
        title={open ? "Kapat" : "AI Asistan — verinle konuş"}
      >
        {open ? "✕" : <IcChat size={20} />}
      </button>
    </div>
  );
}
